import React, { Component } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import DaySelector from './DaySelector';
import TimeSelector from './TimeSelector';
import VerbButton, { VERB_BUTTON_TYPE } from '../VerbButton';
import { createSchedule, createCalendarEvent } from '../../actions';
import { RuntimeContext } from '../../../../common/RuntimeContext';

class ScheduleModal extends Component {
    static propTypes = {
        isModalOpen: PropTypes.bool,
        closeModal: PropTypes.func,
        updateSchedule: PropTypes.func,
        modalActiveSlide: PropTypes.number,
        eventInfo: PropTypes.object
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            isOpen: props.isModalOpen || false,
            activeSlide: props.modalActiveSlide || 1,
            days: [],
            hour: '10',
            minute: '0',
            period: 'AM',
            selectedCalendar: '',
            errorMessage: ''
        };
    }

    componentDidMount() {
        const { isModalOpen } = this.props;
        if (isModalOpen) {
            this.setState(() => ({ isOpen: true }));
        }
    }

    componentDidUpdate() {
        const { isModalOpen } = this.props;
        if (isModalOpen && !this.state.isOpen) {
            this.setState(() => ({ isOpen: true }));
        }
        if (this.state.isOpen && !isModalOpen) {
            this.setState(() => ({ isOpen: false }));
        }
    }

    _gethour = (hour, period) => {
        if (Number.isNaN(Number(hour))) {
            return 0;
        }

        if (period === 'PM') {
            return hour + 12;
        }

        return hour;
    };

    _createIcs = (isGoogle = false) => {
        const { updateSchedule, eventInfo } = this.props;
        const { days, hour, minute, period } = this.state;
        const scheduledHour = this._gethour(hour, period);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currentDate = new Date();
        const recurrenceRule = `FREQ=WEEKLY;WKST=SU;BYDAY=${days.join(',')}`;
        let params = {
            year: currentDate.getFullYear(),
            month: currentDate.getMonth() + 1, // getMonth is zero-based
            day: currentDate.getDate(),
            hour: scheduledHour,
            minute,
            timezone,
            recurrenceRule,
            isGoogle
        };

        if (eventInfo) {
            const dateFormatted = new Date(eventInfo.dateTime);
            const timezone = /\((.*)\)/.exec(dateFormatted.toString())[1];
            params = {
                year: dateFormatted.getUTCFullYear(),
                month: dateFormatted.getUTCMonth() + 1,
                day: dateFormatted.getUTCDate(),
                hour: dateFormatted.getUTCHours(),
                minute: dateFormatted.getUTCMinutes(),
                title: eventInfo.name,
                description: eventInfo.shortDescription,
                timezone,
                isGoogle
            };
            if (eventInfo.urlLink) {
                params.url = eventInfo.urlLink;
            }
            return createCalendarEvent({ ...params, pathsContext: this.context.pathsWeb });
        }

        return createSchedule(params).then(() => {
            updateSchedule({ hour: scheduledHour, minute, days });
        });
    };

    setDays = (day) => {
        const sortDict = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5 };
        return this.setState((prevstate) => {
            const daysArr = [...prevstate.days];
            for (let i = 0; i < daysArr.length; i += 1) {
                if (day === daysArr[i]) {
                    daysArr.splice(i, 1);
                    return { days: daysArr };
                }
            }
            daysArr.push(day);
            return { days: daysArr.sort((a, b) => sortDict[a] - sortDict[b]) };
        });
    };

    setTime = (time) => {
        const { hour, minute } = time;
        this.setState(() => ({ hour, minute }));
    };

    periodChange = (period) => {
        this.setState(() => ({ period }));
    };

    render() {
        const { isOpen, days, hour, minute, period, activeSlide, showGoogle, showOutlook } = this.state;
        const { closeModal, eventInfo, modalActiveSlide } = this.props;
        return (
            <div>
                <Modal
                    overlayClassName={`schedule-overlay`}
                    className={`schedule-modal modal-container`}
                    isOpen={isOpen && activeSlide === 1}
                    onRequestClose={closeModal}
                >
                    <h2>Just 5 minutes a day...</h2>
                    <p>
                        We retain the most knowledge if we learn just a little bit every day. Set up 5 minutes a day
                        when you can log into Verb and learn something new!
                    </p>
                    <h4>Which days work best for you?</h4>
                    <DaySelector onChange={this.setDays} selectedDays={days} />
                    <h4>What time works best for you?</h4>
                    <TimeSelector
                        periodChange={this.periodChange}
                        timeChange={this.setTime}
                        selectedTime={{ hour, minute }}
                        selectedPeriod={period}
                    />
                    <br />
                    <br />
                    <br />
                    <div className={'row'}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            onClick={closeModal}
                            buttonText={'Cancel'}
                        />
                        <VerbButton
                            onClick={() => {
                                if (this.state.days.length) {
                                    this.setState({ activeSlide: 2, errorMessage: '' });
                                } else {
                                    this.setState({ errorMessage: 'Please select at least one day.' });
                                }
                            }}
                            buttonText={'Next'}
                        />
                    </div>
                    <div className={'error-message'}> {this.state.errorMessage} </div>
                </Modal>
                <Modal
                    overlayClassName={`schedule-overlay`}
                    className={`schedule-modal modal-container`}
                    isOpen={isOpen && activeSlide === 2}
                    onRequestClose={closeModal}
                >
                    <h2>Let's add it to your calendar.</h2>
                    <br />
                    <div className={'row'}>
                        <VerbButton
                            onClick={() => {
                                this.setState({ showGoogle: !showGoogle, showOutlook: false });
                            }}
                            buttonText={'Sync with Google'}
                        />
                        <VerbButton
                            onClick={() => {
                                this.setState({ showOutlook: !showOutlook, showGoogle: false });
                            }}
                            buttonText={'Sync with Outlook'}
                        />
                    </div>
                    {showGoogle ? (
                        <div>
                            <br />
                            <p>1. Download the calendar file.</p>
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                fontAwesomeIcon={'fa-download'}
                                fontAwesomeIconWeight={'fal'}
                                onClick={() => {
                                    this._createIcs(true);
                                }}
                                buttonText={'Download'}
                            />
                            <br />
                            <br />
                            <p>2. Open your Google Calender.</p>
                            <p>3. Open the 'Settings' page.</p>
                            <p>4. Select 'Import & export' from the menu.</p>
                            <p>5. Select the downloaded file, and import.</p>
                        </div>
                    ) : null}
                    {showOutlook ? (
                        <div>
                            <br />
                            <p>1. Download the calendar file.</p>
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                fontAwesomeIcon={'fa-download'}
                                fontAwesomeIconWeight={'fal'}
                                onClick={() => {
                                    this._createIcs();
                                }}
                                buttonText={'Download'}
                            />
                            <br />
                            <br />
                            <p>2. Open Outlook.</p>
                            <p>3. Open 'File' menu and select 'Import and Export'.</p>
                            <p>4. Select 'Import an iCalendar or vCalendar' and click Next.</p>
                            <p>5. Select the downloaded file and Import to add to your calendar.</p>
                        </div>
                    ) : null}
                    <br />
                    <div className={'row'}>
                        {!eventInfo ? (
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                onClick={() => {
                                    this.setState({ activeSlide: 1 });
                                }}
                                fontAwesomeIcon={'fa fa-chevron-left'}
                            />
                        ) : null}
                        <VerbButton
                            onClick={() => {
                                this.setState({ activeSlide: modalActiveSlide || 1 });
                                closeModal();
                            }}
                            buttonText={'Done'}
                        />
                    </div>
                </Modal>
            </div>
        );
    }
}

export default ScheduleModal;
